/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import {
  createNewOption,
  CreateSingleDataEntryField,
  createSingleEntryFields,
  deleteDropdown,
  deleteOption,
  deleteSingleEntryFields,
  getAllOption,
  getIssuesConfig,
  getSingleEntryFields,
  updateIssuesConfig,
  updateDropdownValueParent,
  updateOption,
  updateOptionType,
  updateSingleEntryFields
} from 'services/option';
import { TFormOption } from './components/FormOption';
import { compact } from 'lodash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { useRoomContext } from 'contexts/RoomContext';
import { getClients } from 'services/clients';
import axios from 'axios';
import { DEFAULT_ISSUES_UI_CONFIG, IssuesUiConfig } from 'utils/issueDueDateColor';
export type TEditOption = {
  id: number;
  name: string;
  value: string;
  type: string;
};

type TListOption = {
  name: string;
  dropdownValues: TEditOption[];
  options: { id: number; type: string }[];
  id: number;
  required: boolean;
  dropdownOptionClients?: { clientId: number; client: { id: number; clientName: string } }[];
}[];
export type TSingleEntryField = {
  id: number;
  name: string;
  type: string;
  required: boolean;
  entries: string[];
  singleDataEntryClients?: { clientId: number; client: { id: number; clientName: string } }[];
};
export const TABS = {
  DROPDOWN: 0,
  SINGLE_FIELD: 1,
  ISSUES_CONFIG: 2
};
export const useInjection = () => {
  const [listOptions, setListOption] = useState<TListOption>();
  const [singleFields, setSingleFields] = useState<TSingleEntryField[]>([]);
  const [selectedSingleFields, setSelectedSingleFields] = useState<TSingleEntryField>();
  const [isOpenModalDropdown, setIsOpenModalDropdown] = useState(false);
  const [isOpenModalSingleField, setIsOpenModalSingleField] = useState(false);
  const [tab, setTab] = useState(TABS.DROPDOWN);
  const { setFlash } = useFlash();
  const { refreshOptions } = useRoomContext();
  const [clientOptions, setClientOptions] = useState<{ value: string; label: string }[]>([]);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [issuesConfigLoading, setIssuesConfigLoading] = useState(false);

  const handleGetClients = useCallback(async () => {
    try {
      const data = await getClients({ page: 1, size: 500 });
      const clients = data?.clients || data?.data || [];
      setClientOptions(clients.map((c: any) => ({ value: String(c.id), label: c.clientName })));
    } catch (e) {
      console.error('Failed to fetch clients', e);
    }
  }, []);

  const handleGetAllOption = useCallback(async () => {
    const data = (await getAllOption()) as { metadata: TListOption };
    setListOption(data.metadata);
  }, []);

  const { asyncCallback: asyncHandleGetAllOption } = useAsyncCallback(handleGetAllOption, []);
  const handleGetSingleFields = useCallback(async () => {
    const data = (await getSingleEntryFields()) as { singleEntryFields: TSingleEntryField[] };
    setSingleFields(data.singleEntryFields);
  }, []);

  const { asyncCallback: asyncHandleGetSingleFields } = useAsyncCallback(handleGetSingleFields, []);

  useEffect(() => {
    asyncHandleGetAllOption();
    asyncHandleGetSingleFields();
    handleGetClients();
    setIssuesConfigLoading(true);
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG))
      .finally(() => setIssuesConfigLoading(false));
  }, [asyncHandleGetAllOption, asyncHandleGetSingleFields, handleGetClients]);

  const handleSaveIssuesConfig = useCallback(async () => {
    try {
      if (issuesConfig.nearNearDueDays < 0 || issuesConfig.nearDueDays < 0) {
        setFlash({ type: 'error', message: 'Day thresholds must be 0 or greater.' });
        return;
      }
      if (issuesConfig.nearNearDueDays > issuesConfig.nearDueDays) {
        setFlash({
          type: 'error',
          message: 'Urgent window must be less than or equal to the Due soon window.'
        });
        return;
      }
      const levels = issuesConfig.criticalityLevels || [];
      const keys = levels.map((l) => l.key).filter(Boolean);
      if (new Set(keys).size !== keys.length) {
        setFlash({ type: 'error', message: 'Criticality level keys must be unique.' });
        return;
      }
      if (!levels.length) {
        setFlash({ type: 'error', message: 'At least one criticality level is required.' });
        return;
      }
      setIssuesConfigLoading(true);
      const res = await updateIssuesConfig({
        ...issuesConfig,
        adHocAutomaticNotificationsEnabled: issuesConfig.issueAutomaticNotificationsEnabled
      });
      setIssuesConfig(res?.config || issuesConfig);
      setFlash({ type: 'success', message: 'Issues config updated successfully!' });
    } catch (error: any) {
      setFlash({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to update issues config'
      });
    } finally {
      setIssuesConfigLoading(false);
    }
  }, [issuesConfig, setFlash]);

  const handleCreateDropdown = useCallback(
    async (payload: { type: string; name: string; clientIds?: number[] }) => {
      try {
        await axios.post('/system/options', payload);
        asyncHandleGetAllOption();
        refreshOptions?.();
        setFlash({ type: 'success', message: 'Created a new Dropdown successfully!' });
      } catch (error: any) {
        setFlash({ type: 'error', message: error?.response?.data?.message });
      }
    },
    [asyncHandleGetAllOption, refreshOptions, setFlash]
  );

  const handleCreateSingleField = useCallback(
    async (payload: CreateSingleDataEntryField) => {
      try {
        await createSingleEntryFields(payload);
        if (tab !== TABS.SINGLE_FIELD) {
          setTab(TABS.SINGLE_FIELD);
        }
        asyncHandleGetSingleFields();
        refreshOptions?.();
        setFlash({ type: 'success', message: 'Created a new single field successfully!' });
      } catch (error: any) {
        setFlash({ type: 'error', message: error?.response?.data?.message });
      }
    },
    [asyncHandleGetSingleFields, refreshOptions, setFlash, tab]
  );

  const handleUpdateSingleField = useCallback(
    async (id: number, payload: Partial<CreateSingleDataEntryField>) => {
      try {
        setSelectedSingleFields(undefined);
        await updateSingleEntryFields(id, payload);
        asyncHandleGetSingleFields();
        refreshOptions?.();
        setFlash({ type: 'success', message: 'Update single field successfully!' });
      } catch (error: any) {
        setFlash({ type: 'error', message: error?.response?.data?.message });
      }
    },
    [asyncHandleGetSingleFields, refreshOptions, setFlash]
  );

  const handleDeleteSingleField = useCallback(
    async (id: number) => {
      if (!confirm('Are you sure you want to delete this single field?')) return;
      try {
        await deleteSingleEntryFields(id);
        asyncHandleGetSingleFields();
        refreshOptions?.();
        setFlash({ type: 'success', message: 'Delete single field successfully!' });
      } catch (error: any) {
        setFlash({ type: 'error', message: error?.response?.data?.message });
      }
    },
    [asyncHandleGetSingleFields, refreshOptions, setFlash]
  );

  const handleCreateNewOption = useCallback(
    async (value: TFormOption) => {
      const optionType = value.type;
      const initialOptions = listOptions?.find((item) => item.id === value.id);

      const optionIds = value.options.filter((item) => {
        if (!item.id) return false;
        const initial = initialOptions?.dropdownValues.find((opt) => opt.id === Number(item.id));
        return initial && (initial.name !== item.name || initial.value !== item.value);
      });
      const payloadUpdateDropdownValueOption: Record<string, string | boolean | number[]> = {};
      if (initialOptions?.required !== value.required) {
        payloadUpdateDropdownValueOption['required'] = value.required;
      }
      if (initialOptions?.name !== value.groupName) {
        payloadUpdateDropdownValueOption['name'] = value.groupName;
      }
      if (value.clientIds !== undefined) {
        payloadUpdateDropdownValueOption['clientIds'] = value.clientIds;
      }
      if (initialOptions?.id && Object.keys(payloadUpdateDropdownValueOption).length) {
        const payload = { id: initialOptions.id!, ...payloadUpdateDropdownValueOption } as {
          id: number;
        } & Record<string, string | boolean | number[]>;
        await updateDropdownValueParent(payload);
      }
      const defaultType = initialOptions?.options
        ?.sort()
        ?.map((op) => op.type)
        .join('');
      const newType = optionType.sort().join('');
      if (initialOptions?.id && defaultType !== newType)
        await updateOptionType(optionType, initialOptions.id);

      // Only update if changed
      const updatePromises = optionIds.map((item) =>
        updateOption({
          id: Number(item.id),
          payload: {
            name: item.name,
            value: item.value
          }
        })
      );

      const newOptions = compact(
        value.options.map((item) => {
          if (item.id) return;
          return {
            value: item.value,
            name: item.name,
            dropdownValueOptionId: initialOptions!.id!
          };
        })
      );
      const deleteOptions = value.deleteOptionIds?.['groupName'] || [];

      await Promise.all([
        ...updatePromises,
        newOptions.length ? createNewOption(newOptions) : Promise.resolve(),
        ...deleteOptions.map((id) => deleteOption({ id }))
      ]);
      await asyncHandleGetAllOption();
      refreshOptions?.();
    },
    [asyncHandleGetAllOption, listOptions, refreshOptions]
  );
  const { asyncCallback: asyncHandleCreateNewOption } = useAsyncCallback(handleCreateNewOption, []);

  return {
    listOptions,
    singleFields,
    clientOptions,
    isOpenModalDropdown,
    handleCreateNewOption: asyncHandleCreateNewOption,
    onToggleModalDropdown: useCallback(() => setIsOpenModalDropdown((prev) => !prev), []),
    asyncHandleGetAllOption,
    handleCreateDropdown,
    handleCreateSingleField,
    handleUpdateSingleField,
    handleDeleteSingleField,
    isOpenModalSingleField,
    setIsOpenModalSingleField,
    tab,
    setTab,
    issuesConfig,
    setIssuesConfig,
    issuesConfigLoading,
    handleSaveIssuesConfig,
    selectedSingleFields,
    setSelectedSingleFields,
    onDeleteDropdown: useCallback(
      async (id: number) => {
        try {
          if (window.confirm(`Are you sure you want to delete this dropdown?`)) {
            await deleteDropdown({ id });
            setFlash({ type: 'success', message: 'Dropdown deleted successfully!' });
            await asyncHandleGetAllOption();
            refreshOptions?.();
          }
        } catch (error: any) {
          setFlash({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to delete dropdown'
          });
        }
      },
      [setFlash, asyncHandleGetAllOption, refreshOptions]
    )
  };
};
